import axios from "../../../api/axios";
import { toast } from "react-toastify";

export const INVOICE_ENDPOINTS = {
  INVOICES_BASE: "/v2/invoices/invoice",
  INVOICE_BY_ID: "/v2/invoices/invoice/:id",
  INVOICE_DOCUMENT: "/v2/invoices/invoice/:id/document",
  INVOICE_STATUS_COUNTS: "/v2/invoices/invoice/status-counts",
  INVOICE_REVIEW_QUEUE: "/v2/invoices/invoice/review-queue",
  INVOICE_REVIEW: "/v2/invoices/invoice/:id/review",
  INVOICE_ELIGIBILITY: "/v2/invoices/invoice/eligibility",
  INVOICE_NEXT_NO: "/v2/invoices/invoice/next-no",
  INVOICE_APPROVE: "/v2/invoices/invoice/:id/approve",
  INVOICE_DECLINE: "/v2/invoices/invoice/:id/decline",
  INVOICE_MARK_PAID: "/v2/invoices/invoice/:id/mark-paid",
  INVOICE_ENTERED_ACCT: "/v2/invoices/invoice/:id/entered-acct",
  INVOICE_BULK_MARK_PAID: "/v2/invoices/invoice/bulk-mark-paid",
  INVOICE_BULK_APPROVE: "/v2/invoices/invoice/bulk-approve",
  INVOICE_STATUS: "/v2/invoices/invoice/:id/status",
  INVOICE_COMMENTS: "/v2/invoices/invoice/:id/comments",
  INVOICE_SIGNED_URLS: "/v2/invoices/invoice/signed-urls",
  INVOICE_UPLOAD_REQUEST: "/v2/invoices/invoice/:id/attachments/request-upload",
  INVOICE_CONFIRM_UPLOAD: "/v2/invoices/invoice/:id/attachments/confirm",
  INVOICE_REMOVE_ATTACHMENT: "/v2/invoices/invoice/:id/attachments/remove",
  INVOICE_AI_EXTRACT: "/v2/invoices/invoice/ai/extract",
  INVOICE_STAGING_UPLOAD_REQUEST: "/v2/invoices/invoice/attachments/staging/request-upload",
  INVOICE_STAGING_SIGNED_URLS: "/v2/invoices/invoice/attachments/staging/signed-urls",
  INVOICE_STAGING_REMOVE: "/v2/invoices/invoice/attachments/staging/remove",
  INVOICE_FORWARD: "/v2/invoices/invoice/:id/forward",

  CATEGORIES_BASE: "/v2/invoices/category",
  CATEGORY_BY_ID: "/v2/invoices/category/:id",

  VENDORS_BASE: "/v2/invoices/vendor",
  VENDOR_BY_ID: "/v2/invoices/vendor/:id",

  AUDITORS_BASE: "/v2/invoices/auditor",
  AUDITOR_BY_ID: "/v2/invoices/auditor/:id",

  SETTINGS_BASE: "/v2/invoices/settings",

  PAYROLL_BASE: "/v2/invoices/payroll",
  PAYROLL_BY_ID: "/v2/invoices/payroll/:id",
  PAYROLL_SEED: "/v2/invoices/payroll/seed-defaults",
};

const handleApiError = (error, defaultMessage) => {
  const message =
    error.response?.data?.message || error.message || defaultMessage || "Unexpected error";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

// ── Invoices ─────────────────────────────────────────────────────────────
export const getInvoices = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICES_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoices"); }
};

export const getInvoiceById = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.get(url);
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice"); }
};

export const getInvoiceStatusCounts = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_STATUS_COUNTS, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice status counts"); }
};

// ── Vendor-submission review queue ─────────────────────────────────────────
export const getInvoiceReviewQueue = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_REVIEW_QUEUE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch review queue"); }
};

export const reviewInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_REVIEW.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to submit review"); }
};

// ── Approval workflow ───────────────────────────────────────────────────────
// Tells the current user whether they may generate an invoice, and (for vendors)
// which properties/buildings they're allowed to invoice.
export const getInvoiceEligibility = async () => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_ELIGIBILITY);
    return res.data?.data || { canCreate: false, properties: [] };
  } catch (e) {
    handleApiError(e, "Failed to check invoice access");
    return { canCreate: false, properties: [] };
  }
};

// Technical Lead (or company) approves a pending invoice.
export const approveInvoice = async (id, payload = {}) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_APPROVE.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to approve invoice"); }
};

// Technical Lead (or company) declines a pending invoice (reason required).
export const declineInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_DECLINE.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to decline invoice"); }
};

// Final Auditor (or company) settles an approved invoice.
export const markInvoicePaid = async (id, payload = {}) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_MARK_PAID.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to mark invoice paid"); }
};

export const setInvoiceEnteredAcct = async (id, entered) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_ENTERED_ACCT.replace(":id", id);
    const res = await axios.patch(url, { entered }, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update accounting status"); }
};

export const bulkMarkInvoicesPaid = async (ids) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_BULK_MARK_PAID, { ids }, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to mark invoices paid"); }
};

// Technical Lead (or company) approves many pending invoices at once.
export const bulkApproveInvoices = async (ids, note = "") => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_BULK_APPROVE, { ids, note }, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to approve invoices"); }
};

export const createInvoice = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICES_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create invoice"); }
};

export const updateInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update invoice"); }
};

export const updateInvoiceStatus = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_STATUS.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update invoice status"); }
};

export const addInvoiceComment = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_COMMENTS.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add comment"); }
};

export const deleteInvoice = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete invoice"); }
};

export const forwardInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_FORWARD.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to forward invoice"); }
};

// ── Attachments ───────────────────────────────────────────────────────────
export const requestInvoiceUploadUrl = async (invoiceId, fileName, contentType) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_UPLOAD_REQUEST.replace(":id", invoiceId);
    const res = await axios.post(url, { fileName, contentType });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to obtain upload URL"); }
};

export const confirmInvoiceUpload = async (invoiceId, key) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_CONFIRM_UPLOAD.replace(":id", invoiceId);
    const res = await axios.post(url, { key });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to confirm upload"); }
};

export const removeInvoiceAttachment = async (invoiceId, key) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_REMOVE_ATTACHMENT.replace(":id", invoiceId);
    const res = await axios.post(url, { key });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to remove attachment"); }
};

export const getInvoiceSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_SIGNED_URLS, { keys });
    return res.data.data;
  } catch (e) {
    handleApiError(e, "Failed to generate signed URLs");
    return {};
  }
};

// ── Categories ────────────────────────────────────────────────────────────
export const getInvoiceCategories = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.CATEGORIES_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice categories"); }
};

export const createInvoiceCategory = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.CATEGORIES_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create category"); }
};

export const updateInvoiceCategory = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.CATEGORY_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update category"); }
};

export const deleteInvoiceCategory = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.CATEGORY_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete category"); }
};

// ── Vendors ───────────────────────────────────────────────────────────────
export const getInvoiceVendors = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.VENDORS_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch vendors"); }
};

export const createInvoiceVendor = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.VENDORS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create vendor"); }
};

export const updateInvoiceVendor = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update vendor"); }
};

export const deleteInvoiceVendor = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete vendor"); }
};

// ── Auditors ──────────────────────────────────────────────────────────────
export const getInvoiceAuditors = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.AUDITORS_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch auditors"); }
};

export const createInvoiceAuditor = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.AUDITORS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add auditor"); }
};

export const updateInvoiceAuditor = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.AUDITOR_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update auditor"); }
};

export const deleteInvoiceAuditor = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.AUDITOR_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete auditor"); }
};

// ── Payment Settings ──────────────────────────────────────────────────────
export const getInvoicePaymentSettings = async () => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.SETTINGS_BASE);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch payment settings"); }
};

export const saveInvoicePaymentSettings = async (payload) => {
  try {
    const res = await axios.put(INVOICE_ENDPOINTS.SETTINGS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to save payment settings"); }
};

// ── Helpers ───────────────────────────────────────────────────────────────
// Canonical approval lifecycle. Pending → Approved → Paid, with Declined as a
// terminal branch off Pending.
export const INVOICE_STATUSES = [
  { value: "Pending", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  { value: "Approved", color: "#4F46E5", bg: "#EEF2FF", border: "#C7D2FE" },
  { value: "Declined", color: "#DC2626", bg: "#FEF2F2", border: "#FECDD3" },
  { value: "Paid", color: "#059669", bg: "#ECFDF5", border: "#6EE7B7" },
];

// Neutral fallback so legacy rows (Unpaid/Overdue) still render a badge.
export const STATUS_FALLBACK = { color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };

export const statusStyle = (status) =>
  INVOICE_STATUSES.find((s) => s.value === status) || { value: status, ...STATUS_FALLBACK };

export const computeLineTotal = (hours, minutes, amountPerHour, materialCost) => {
  const h = Number.isFinite(Number(hours)) ? Number(hours) : 0;
  const m = minutes === "" || minutes == null || !Number.isFinite(Number(minutes)) ? 0 : Number(minutes);
  const r = Number.isFinite(Number(amountPerHour)) ? Number(amountPerHour) : 0;
  const mat = materialCost === "" || materialCost == null || !Number.isFinite(Number(materialCost)) ? 0 : Number(materialCost);
  // Labour counts when a rate is set and EITHER hours or minutes is entered.
  const noTime = (hours === "" || hours == null) && (minutes === "" || minutes == null);
  const labor = noTime || amountPerHour === "" || amountPerHour == null ? 0 : (h + m / 60) * r;
  return Math.round((labor + mat) * 100) / 100;
};

// GST breakdown for a single property line.
export const computeLineMoney = (amount, gstRate) => {
  const subtotal = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const rate = Number.isFinite(Number(gstRate)) ? Number(gstRate) : 5;
  const gstAmount = Math.round(subtotal * rate) / 100;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    gstRate: rate,
    gstAmount: Math.round(gstAmount * 100) / 100,
    total: Math.round((subtotal + gstAmount) * 100) / 100,
  };
};

// Mirrors canEditInvoiceInStatus on the server. Kept as an allow-list for the
// same reason: an Approved or Paid invoice is settled, and anything unfamiliar
// (including the legacy Unpaid/Overdue values) is treated as locked. This only
// decides whether the button is offered — the server is what enforces it.
const EDITABLE_STATUSES = ["Pending", "Declined"];
const VENDOR_EDITABLE_STATUSES = ["Pending"];

export const isInvoiceEditable = (invoice, userRole) => {
  const status = invoice?.status;
  const isVendor = String(userRole || "").toLowerCase() === "vendor";
  const allowed = isVendor ? VENDOR_EDITABLE_STATUSES : EDITABLE_STATUSES;
  return allowed.includes(String(status));
};

// Company-wide sequential number shown everywhere as "RD <n>".
export const formatInvoiceNo = (inv) =>
  inv && inv.invoiceNo != null && inv.invoiceNo !== "" ? `RD ${inv.invoiceNo}` : "—";

// Peek at the next RD number for create-form prefill. Quiet failure — the
// prefill is a best-effort suggestion, never a blocker.
export const getNextInvoiceNoPeek = async (propertyId) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_NEXT_NO, {
      // A vendor account has no company of its own — the property tells the
      // server whose numbering sequence to read.
      params: propertyId ? { propertyId } : undefined,
    });
    const n = res?.data?.data?.nextNo;
    return Number.isFinite(Number(n)) ? Number(n) : null;
  } catch {
    return null;
  }
};

export const formatCurrency = (amount, currency = "CAD") => {
  const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: (currency || "CAD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe);
  } catch {
    return `${(currency || "CAD").toUpperCase()} ${safe.toFixed(2)}`;
  }
};

export const uploadFileToInvoice = async (invoiceId, file) => {
  const { signedUrl, key } = await requestInvoiceUploadUrl(invoiceId, file.name, file.type);
  await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  await confirmInvoiceUpload(invoiceId, key);
  return key;
};

export const requestStagingUploadUrl = async (fileName, contentType) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_UPLOAD_REQUEST, { fileName, contentType });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to obtain upload URL"); }
};

export const getStagingSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_SIGNED_URLS, { keys });
    return res.data.data;
  } catch (e) {
    handleApiError(e, "Failed to generate signed URLs");
    return {};
  }
};

export const removeStagingObject = async (key) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_REMOVE, { key });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to remove file"); }
};

export const uploadStagingFile = async (file) => {
  const { signedUrl, key } = await requestStagingUploadUrl(file.name, file.type);
  await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  return key;
};

// Reads a staged invoice file with the AI and returns suggested form values.
// Never toasts — the caller (the form) decides how to surface success/failure so
// autofill stays a quiet, optional enhancement. Returns null when the AI could
// not produce a usable suggestion.
export const extractInvoiceFromFile = async (key) => {
  try {
    const res = await axios.post(
      INVOICE_ENDPOINTS.INVOICE_AI_EXTRACT,
      { key },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.data || null;
  } catch (e) {
    console.warn("Invoice AI extract failed", e?.response?.data || e?.message);
    return null;
  }
};

// ── Payroll calendar ─────────────────────────────────────────────────────
export const getPayrollCycles = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.PAYROLL_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch payroll calendar"); }
};

export const createPayrollCycle = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.PAYROLL_BASE, payload);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add payroll cycle"); }
};

export const updatePayrollCycle = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.PAYROLL_BY_ID.replace(":id", id);
    const res = await axios.put(url, payload);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update payroll cycle"); }
};

export const deletePayrollCycle = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.PAYROLL_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to remove payroll cycle"); }
};

export const seedPayrollCycles = async () => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.PAYROLL_SEED);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to load the default schedule"); }
};
